import React from "react";
import { useFormContext } from "react-hook-form";
import type { FilterForm } from "./logsShared";
import { MIN_SEARCH_LENGTH } from "./logsQuery";

export interface PatientChip {
  id: string;
  name: string;
}

interface LogsSearchControlsProps {
  patientChip: PatientChip | null;
  onClearPatient: () => void;
}

const INPUT =
  "px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";

/**
 * Search box (patient name or Reg No) + the "Patient: X" chip set by clicking
 * a name in the table. Lives inside the page's FormProvider; Apply submits it.
 */
function LogsSearchControls({ patientChip, onClearPatient }: LogsSearchControlsProps) {
  const { register, watch } = useFormContext<FilterForm>();
  const search = (watch("search") || "").trim();
  const tooShort = search.length > 0 && search.length < MIN_SEARCH_LENGTH;

  return (
    <>
      <div className="flex flex-col">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Patient</label>
        <input
          type="search"
          placeholder="Name or Reg No"
          autoComplete="off"
          className={`${INPUT} min-w-[200px]`}
          {...register("search")}
        />
        {tooShort && (
          <span className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
            Type at least {MIN_SEARCH_LENGTH} characters
          </span>
        )}
      </div>
      {patientChip && (
        <div className="flex flex-col">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Only</span>
          <span className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm bg-primary/10 dark:bg-slate-700 text-primary dark:text-slate-200 border border-primary/20 dark:border-slate-600 whitespace-nowrap">
            {patientChip.name}
            <button
              type="button"
              aria-label="Clear patient filter"
              title="Clear patient filter"
              className="ml-1 leading-none hover:text-red-600"
              onClick={onClearPatient}
            >
              ×
            </button>
          </span>
        </div>
      )}
    </>
  );
}

export default LogsSearchControls;
